import React from 'react';
import { UserProtocol } from '../../../types/protocols';
import './ProtocolPreviewDialog.scss';

interface ProtocolPreviewDialogProps {
  protocol: UserProtocol;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (id: number) => void;
}

const ProtocolPreviewDialog: React.FC<ProtocolPreviewDialogProps> = ({
  protocol,
  isOpen,
  onClose,
  onEdit,
}) => {
  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const parseProtocolJson = () => {
    try {
      return JSON.parse(protocol.content); // Заменили json_protocol на content
    } catch (error) {
      console.error('Failed to parse protocol JSON:', error);
      return null;
    }
  };

  const protocolData = parseProtocolJson();

  const renderFields = () => {
    if (!protocolData || !protocolData.fields) return null;

    const blocks: any[] = [];
    const standaloneFields: any[] = [];

    // Разделяем блоки и отдельные поля
    Object.entries(protocolData.fields).forEach(([fieldKey, fieldConfig]: [string, any]) => {
      if (fieldKey === 'LAZYDOC_NOTES') return; // Пропускаем системные поля

      if (fieldConfig.type === 'block') {
        // Это блок
        blocks.push({
          key: fieldKey,
          title: fieldConfig.label,
          order: fieldConfig.order || 0,
          fields: fieldConfig.fields || {}
        });
      } else {
        // Это отдельное поле
        standaloneFields.push([fieldKey, fieldConfig]);
      }
    });

    // Сортируем блоки по order
    blocks.sort((a, b) => a.order - b.order);

    return (
      <>
        {/* Отображаем блоки */}
        {blocks.map((block: any) => (
          <div key={block.key} className="protocol-block">
            <h4 className="block-title">{block.title}</h4>
            <div className="block-fields">
              {Object.entries(block.fields).map(([fieldName, field]: [string, any]) => (
                <div key={fieldName} className="field-item">
                  <div className="field-header">
                    <span className="field-label">{field.label}</span>
                    <span className={`field-type ${field.type}`}>{getFieldTypeLabel(field.type)}</span>
                    {field.required && <span className="field-required">Обязательное</span>}
                  </div>
                  {field.description && (
                    <p className="field-description">{field.description}</p>
                  )}
                  {field.default && (
                    <p className="field-default">
                      <strong>По умолчанию:</strong> {field.default}
                    </p>
                  )}
                  {field.choices && field.choices.length > 0 && (
                    <div className="field-choices">
                      <strong>Варианты выбора:</strong>
                      <ul>
                        {field.choices.map((choice: string, index: number) => (
                          <li key={index}>{choice}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {field.range && (
                    <p className="field-range">
                      <strong>Диапазон:</strong> от {field.range[0]} до {field.range[1]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Отображаем отдельные поля, если есть */}
        {standaloneFields.length > 0 && (
          <div className="protocol-block">
            <h4 className="block-title">Дополнительные поля</h4>
            <div className="block-fields">
              {standaloneFields.map(([fieldName, field]: [string, any]) => (
                <div key={fieldName} className="field-item">
                  <div className="field-header">
                    <span className="field-label">{field.label}</span>
                    <span className={`field-type ${field.type}`}>{getFieldTypeLabel(field.type)}</span>
                    {field.required && <span className="field-required">Обязательное</span>}
                  </div>
                  {field.description && (
                    <p className="field-description">{field.description}</p>
                  )}
                  {field.default && (
                    <p className="field-default">
                      <strong>По умолчанию:</strong> {field.default}
                    </p>
                  )}
                  {field.choices && field.choices.length > 0 && (
                    <div className="field-choices">
                      <strong>Варианты выбора:</strong>
                      <ul>
                        {field.choices.map((choice: string, index: number) => (
                          <li key={index}>{choice}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {field.range && (
                    <p className="field-range">
                      <strong>Диапазон:</strong> от {field.range[0]} до {field.range[1]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  const getFieldTypeLabel = (type: string) => {
    const typeLabels: { [key: string]: string } = {
      'free-text': 'Текст',
      'number': 'Число',
      'select': 'Выбор',
      'checkbox': 'Чекбокс',
      'date': 'Дата',
    };
    return typeLabels[type] || type;
  };

  return (
    <div className="protocol-preview-overlay" onClick={onClose}>
      <div className="protocol-preview-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <div className="dialog-title">
            <h2>{protocol.name}</h2>
            <div className="protocol-meta">
              <span className={`status-badge ${protocol.is_active ? 'active' : 'inactive'}`}>
                {protocol.is_active ? 'Активен' : 'Неактивен'}
              </span>
              {protocolData?.meta?.specialty && (
                <span className="specialty-badge">{protocolData.meta.specialty}</span>
              )}
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" />
              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>

        <div className="dialog-content">
          {protocol.description && (
            <div className="protocol-description">
              <h3>Описание</h3>
              <p>{protocol.description}</p>
            </div>
          )}

          {protocolData?.['x-instructions'] && (
            <div className="protocol-instructions">
              <h3>Инструкции</h3>
              <div className="instructions-content">
                {protocolData['x-instructions'].split('\n').map((line: string, index: number) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
            </div>
          )}

          <div className="protocol-fields">
            <h3>Структура шаблона</h3>
            {renderFields()}
          </div>

          <div className="protocol-info">
            <div className="info-row">
              <span className="info-label">Создан:</span>
              <span className="info-value">{formatDate(protocol.created_at)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Обновлен:</span>
              <span className="info-value">{formatDate(protocol.updated_at)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Количество полей:</span>
              <span className="info-value">{Object.keys(protocolData?.fields || {}).length - 1}</span>
            </div>
          </div>
        </div>

        <div className="dialog-footer">
          <button className="button-secondary" onClick={onClose}>
            Закрыть
          </button>
          <button
            className="button-primary"
            onClick={() => {
              onEdit(protocol.id);
              onClose();
            }}
          >
            Редактировать
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProtocolPreviewDialog;
